import { apiBaseURL } from '@/api/client'
import { useAuthStore } from '@/stores/auth.store'
import { useQueryClient } from '@tanstack/react-query'
import ReconnectingWebSocket from 'partysocket/ws'
import { useEffect, useRef } from 'react'

type DomainEvent =
  | {
      type: 'quote_created' | 'quote_updated' | 'quote_deleted'
      resource_id: string
      payload?: unknown
    }
  | { type: 'booking_created' | 'booking_updated'; resource_id: string; payload?: unknown }
  | { type: 'shipment_created' | 'shipment_updated'; resource_id: string; payload?: unknown }
  | { type: 'invoice_created' | 'invoice_updated'; resource_id: string; payload?: unknown }
  | { type: 'payment_created' | 'payment_updated'; resource_id: string; payload?: unknown }

function wsBase(): string {
  return apiBaseURL.replace(/^http:\/\//, 'ws://').replace(/^https:\/\//, 'wss://')
}

/**
 * Opens a single WebSocket connection to /ws/events and invalidates the
 * TanStack Query cache on every event. Mount once in the authenticated
 * layout — components re-render via their own useQuery hooks.
 */
export function useRealtimeEvents(): void {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((s) => s.accessToken)
  const socketRef = useRef<ReconnectingWebSocket | null>(null)

  useEffect(() => {
    if (!accessToken) return

    const url = `${wsBase()}/ws/events?token=${encodeURIComponent(accessToken)}`

    const socket = new ReconnectingWebSocket(url, [], {
      maxRetries: Number.POSITIVE_INFINITY,
      minReconnectionDelay: 1000,
      maxReconnectionDelay: 30_000,
      connectionTimeout: 10_000,
    })

    const onMessage = (event: MessageEvent<string>) => {
      let msg: DomainEvent
      try {
        msg = JSON.parse(event.data) as DomainEvent
      } catch {
        return
      }

      switch (msg.type) {
        case 'quote_created':
          void queryClient.invalidateQueries({ queryKey: ['quotes', 'list'] })
          break
        case 'quote_updated':
          void queryClient.invalidateQueries({ queryKey: ['quotes', 'detail', msg.resource_id] })
          void queryClient.invalidateQueries({ queryKey: ['quotes', 'list'] })
          break
        case 'quote_deleted':
          queryClient.removeQueries({ queryKey: ['quotes', 'detail', msg.resource_id] })
          void queryClient.invalidateQueries({ queryKey: ['quotes', 'list'] })
          break

        case 'booking_created':
          void queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] })
          break
        case 'booking_updated':
          void queryClient.invalidateQueries({ queryKey: ['bookings', 'detail', msg.resource_id] })
          void queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] })
          break

        case 'shipment_created':
          void queryClient.invalidateQueries({ queryKey: ['shipments', 'list'] })
          void queryClient.invalidateQueries({ queryKey: ['shipments', 'stats'] })
          break
        case 'shipment_updated':
          void queryClient.invalidateQueries({ queryKey: ['shipments', 'detail', msg.resource_id] })
          void queryClient.invalidateQueries({ queryKey: ['shipments', 'list'] })
          void queryClient.invalidateQueries({ queryKey: ['shipments', 'stats'] })
          break

        case 'invoice_created':
        case 'invoice_updated':
          void queryClient.invalidateQueries({ queryKey: ['finance', 'invoice', msg.resource_id] })
          void queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] })
          void queryClient.invalidateQueries({ queryKey: ['finance', 'ar-aging'] })
          break

        case 'payment_created':
        case 'payment_updated':
          void queryClient.invalidateQueries({ queryKey: ['finance', 'payments'] })
          void queryClient.invalidateQueries({ queryKey: ['finance', 'ar-aging'] })
          void queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] })
          break
      }
    }

    socket.addEventListener('message', onMessage as EventListener)
    socketRef.current = socket

    return () => {
      socket.removeEventListener('message', onMessage as EventListener)
      socket.close()
      socketRef.current = null
    }
  }, [accessToken, queryClient])
}
